import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
// import {useHistory} from 'react-router-dom';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

import {updateUser, selectUser, selectIsLoading} from '../../redux/slices/user';
import PrimaryAppBar from './PrimaryAppBar';
import SecondaryAppBar from './SecondayAppBar';
import AutoCompletePlace from './AutoCompletePlace';
import {BluePillButton} from '../../common/components/BluePillButton';

import baseStyles from '../../css/Base.module.css';

const OCCUPATIONS = [
  'Classroom teacher',
  'Principal / Assistant Principal',
  'Specialist Teacher / Coach',
  'Superintendent / Ass. Superintendent',
  'Purchasing / Finance',
  'Curriculum Director',
  'Curriculum Specialist / Coordinator',
  'Educational Technology Director',
  'Ed Tech Specialist / Coordinator',
  'Bilingual / ESL Director',
  'Bilingual / ESL Specialist / Coordinator',
  'Special Ed Director',
  'Special Ed Specialist / Coordinator',
  'Federal Programs Director',
  'Federal Programs Specialist / Coordinator',
  'College Student',
  'College Faculty',
  'Parent',
  'Homeschooler',
  'Other Educator',
];

export default function ProfessionalInformation() {
  const dispatch = useDispatch();
  // const history = useHistory();

  const isLoading = useSelector(selectIsLoading);

  const [hasSetFormData, toggleHasSetFormData] = useState(false);
  const [occupation, setOccupation] = useState('');
  const [workplace, setWorkplace] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [dialogMessage, setDialogMessage] = useState(null);
  const [isWorkplaceRequiredDialogVisible, toggleIsWorkplaceRequiredDialogVisible] = useState(false);
  const user = useSelector(selectUser);

  // Set initial form data
  useEffect(() => {
    if (Object.keys(user).length > 0 && !hasSetFormData) {
      setOccupation(user.professionalInformation?.occupation ?? '');
      setWorkplace(user.professionalInformation?.workplace ?? null);
      toggleHasSetFormData(true);

      if (!user.professionalInformation?.workplace) toggleIsWorkplaceRequiredDialogVisible(true);
    }
  }, [user, hasSetFormData]);

  function handleOccupationChanged(event) {
    setOccupation(event.target.value);
  }

  function isSubmitButtonEnabled() {
    return occupation && workplace;
  }

  function validateForm() {
    const errors = {};

    if (!occupation) errors.occupation = 'This field is required.';

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  }

  async function handleClickSubmit() {
    const isFormValid = validateForm();

    if (isFormValid) {
      try {
        await dispatch(updateUser({professionalInformation: {occupation, workplace}}));
      } catch (error) {
        console.log(`update user error: ${JSON.stringify(error)}`);
        setDialogMessage('An error occurred, try again.');
      }

      setDialogMessage('Your professional information has been updated.');
    }
  }

  function renderWorkplaceRequiredDialog() {
    return (
      <Dialog
        open={isWorkplaceRequiredDialogVisible}
        onClose={() => toggleIsWorkplaceRequiredDialogVisible(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Workplace is Now Required</DialogTitle>
        <DialogContent>
          <Typography>Please enter the name of your current place of work.</Typography>
        </DialogContent>
        <DialogActions>
          {/* <Button onClick={() => history.push('/builder')}>I&apos;ll Do This Later</Button> */}
          <BluePillButton variant="outlined" onClick={() => toggleIsWorkplaceRequiredDialogVisible(false)}>
            OK
          </BluePillButton>
        </DialogActions>
      </Dialog>
    );
  }

  function renderDialog() {
    return (
      <Dialog open={!!dialogMessage} onClose={() => setDialogMessage(null)}>
        <DialogTitle>Professional Information</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMessage(null)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <PrimaryAppBar />
      <SecondaryAppBar title="Professional Information"></SecondaryAppBar>
      {renderDialog()}
      {renderWorkplaceRequiredDialog()}

      {isLoading ? (
        <div className={baseStyles['progress-container']}>
          <CircularProgress />
        </div>
      ) : (
        <Grid container spacing={3} className="p-20">
          <Grid item xs={12}>
            <Typography variant="body1" className="clr-gray-400 p-t-10">
              Help make Flamingo Learning a better product by telling us a little about you.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Grid item md={4} sm={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel variant="standard">Occupation</InputLabel>
                <Select variant="standard" value={occupation} onChange={handleOccupationChanged}>
                  {OCCUPATIONS.map((occupation) => (
                    <MenuItem key={occupation} value={occupation}>
                      {occupation}
                    </MenuItem>
                  ))}
                </Select>
                {!!formErrors.occupation && <FormHelperText error>{formErrors.occupation}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid item md={4} sm={6} xs={12}>
              <AutoCompletePlace isRequired inputLabel="Workplace" onSelect={setWorkplace} initialValue={workplace} />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid item md={4} sm={6} xs={12} className={[baseStyles['text-align-end'], 'm-t-20'].join(' ')}>
              <BluePillButton variant="outlined" onClick={handleClickSubmit} disabled={!isSubmitButtonEnabled()}>
                SUBMIT
              </BluePillButton>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
}
